import { useDrawerEscape } from '../../../lib/hooks/useDrawerEscape';
import { SocialFields } from '../SocialFields';
import { useSocialFields } from '../../../lib/hooks/useSocialFields';
import { normalizeSocialUrl } from '../../../lib/utils/clientSocial';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Building2, User, CreditCard, DollarSign, Layers, Factory, FileText, RotateCcw, Percent } from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import { useUserStore } from '../../../lib/stores/userStore';
import { usePaymentMethodStore } from '../../../lib/stores/paymentMethodStore';
import { useLeadSourceStore } from '../../../lib/stores/leadSourceStore';
import { useCustomStatesStore } from '../../../lib/stores/customStatesStore';
import { useClientTagStore } from '../../../lib/stores/clientTagStore';
import { useReferrerStore } from '../../../lib/stores/referrerStore';
import { useInterestCategoryStore } from '../../../lib/stores/interestCategoryStore';
import { useSettoriStore } from '../../../lib/stores/settoriStore';
import { usePriceListStore } from '../../../lib/stores/priceListStore';
import { useLeadPipelineStore } from '../../../lib/stores/leadPipelineStore';
import { usePermissions } from '../../../lib/hooks/usePermissions';
import { useDuplicateDetection } from '../../../lib/hooks/useDuplicateDetection';
import { DuplicateNotice } from './DuplicateNotice';
import { ReferentiRows } from './ReferentiRows';
import { VatEnrichField, type EnrichResult } from '../VatEnrichField';
import { fiscalVariantForCountry, phonePrefixForCountry } from '../../../lib/utils/countries';
import { DrawerSection } from './DrawerSection';
import { CreateClientTopProps } from './CreateClientTopProps';
import { FiscalSection } from './FiscalSection';
import { useDbColumnSupported } from '../../../lib/hooks/useDbColumnSupported';
import { VAT_RATE_OPTIONS } from '../../../lib/hooks/useProductQuickForm';
import { useWarehouseSettings } from '../../../lib/hooks/useWarehouseSettings';
import { listVatCodes, vatCodeLabel, type VatCode } from '../../../lib/services/vatCodeService';
import { AddressSectionRows } from './AddressSectionRows';
import { OperationalLocationsRows } from './OperationalLocationsRows';
import { ContactRows, ConsentRows } from './MarketingRows';
import { InterestTreeChipRow, MultiChipRow, PropSelectRow, PropTextRow } from './rows';
import { buildInitialFormData, buildSubmissionData, KEEP_ON_CREATE_ANOTHER } from './formDefaults';
import { useCreateClientDraft } from './useCreateClientDraft';
import { pickDefaultPipeline, type PipelineCandidate } from '../../../lib/leads/newLeadPipelineDefaults';

interface CreateClientDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Salvataggio del chiamante; ritorna il cliente creato (o true/false). */
  onComplete: (data: Record<string, any>) => Promise<any>;
  initialData?: Record<string, any>;
  isLead?: boolean;
  isSubmitting?: boolean;
  /** "Crea e apri scheda": il chiamante apre la scheda del cliente creato. */
  onOpenClient?: (clientId: string) => void;
  /**
   * 'edit' = modifica di un'anagrafica esistente: initialData è il cliente
   * (via clientToDrawerInitialData), footer con solo "Salva", niente bozza,
   * check doppioni solo sui campi cambiati, referenti fuori (si gestiscono da "Persone").
   */
  mode?: 'create' | 'edit';
}

type SubmitMode = 'close' | 'open' | 'another';

/**
 * Drawer Notion-like "Nuova anagrafica": sostituisce il wizard modale a 5 step
 * nei punti di creazione. Stessa shape dati e stesso onComplete del wizard.
 * Design: MD/plans/2026-08-13-anagrafica-internazionale-drawer.md
 */
export function CreateClientDrawer({
  isOpen,
  onClose,
  onComplete,
  initialData,
  isLead = false,
  isSubmitting = false,
  onOpenClient,
  mode = 'create',
}: CreateClientDrawerProps) {
  // Aliquota IVA del cliente: campo facoltativo, visibile dopo la migration 20260916d
  const vatRateSupported = useDbColumnSupported('clients', 'default_vat_rate');
  // Codice IVA (esenzione): solo org con «Codici IVA dei clienti» attivo (Impostazioni → Offerte)
  const vatCodeColumnSupported = useDbColumnSupported('clients', 'vat_code_id');
  const { client_vat_codes_enabled: vatCodesEnabled } = useWarehouseSettings();
  const vatCodesOn = vatCodeColumnSupported && vatCodesEnabled;
  const [vatCodes, setVatCodes] = useState<VatCode[]>([]);
  useEffect(() => {
    if (!vatCodesOn || !isOpen) return;
    listVatCodes().then(setVatCodes).catch(() => setVatCodes([]));
  }, [vatCodesOn, isOpen]);
  const isEdit = mode === 'edit';
  const { user } = useAuth();
  const { users, fetchUsers } = useUserStore();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethodStore();
  const { fetchLeadSources } = useLeadSourceStore();
  const { clientStates, leadStates, fetchClientStates, fetchLeadStates } = useCustomStatesStore();
  const { tags, fetchTags, createTag } = useClientTagStore();
  const { settori, fetchSettori } = useSettoriStore();
  const { fetchReferrers } = useReferrerStore();
  const { fetchCategories: fetchInterestCategories } = useInterestCategoryStore();
  const { priceLists, fetchPriceLists } = usePriceListStore();
  // La mappa `stages` serve nelle dipendenze dell'effect fase-default:
  // getStagesForPipeline è una funzione stabile e da sola non ri-esegue
  // l'effect quando le fasi arrivano dal fetch (stesso bug di leadStagesFromDb).
  const { pipelines, fetchPipelines, fetchStagesByPipeline, getStagesForPipeline, stages: stagesByPipeline } = useLeadPipelineStore();
  const { canAccessPaymentMethods, canAccessInventory } = usePermissions();
  const { duplicateResult, triggerDuplicateCheck, resetDuplicateCheck } = useDuplicateDetection();
  const { loadDraft, saveDraft, clearDraft } = useCreateClientDraft(user?.id, isLead);

  const [formData, setFormData] = useState<Record<string, any>>(() => buildInitialFormData(initialData));
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const resetRef = useRef<HTMLDivElement>(null);
  const dirtyRef = useRef(false);
  // Ref sempre allineata a formData: i callback registrati UNA volta (listener
  // place_changed di Google Places, enrich) catturano una versione stale di
  // updateFormData — senza la ref un loro update tardivo ripartirebbe dal form
  // di quel momento azzerando tutto ciò che è stato scritto dopo.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Fetch dati di supporto una volta per apertura
  useEffect(() => {
    if (!isOpen) return;
    fetchUsers(); fetchPaymentMethods(); fetchLeadSources(); fetchClientStates();
    fetchLeadStates(); fetchTags(); fetchSettori(); fetchReferrers();
    fetchInterestCategories(); fetchPriceLists(); fetchPipelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Modifica: ad ogni apertura il form riparte dal cliente corrente (il record
  // può essere cambiato dall'ultima apertura — edit inline, realtime, ecc.)
  useEffect(() => {
    if (!isOpen || !isEdit) return;
    setFormData(buildInitialFormData(initialData));
    setValidationErrors({});
    dirtyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, initialData?.id]);

  // Creazione con prefill (es. dal form veloce dell'offerta o dalla chat WhatsApp):
  // il drawer resta montato con isOpen=false, quindi ad ogni apertura il form
  // riparte dai dati passati (nome, email, telefono) invece che dallo stato al mount.
  const hasCreatePrefill = !!(initialData && !initialData.id && (
    initialData.business_name || initialData.email || initialData.phone || initialData.whatsapp_number
  ));
  useEffect(() => {
    if (!isOpen || isEdit || !hasCreatePrefill) return;
    setFormData(buildInitialFormData(initialData));
    setValidationErrors({});
    dirtyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Ripristino bozza (solo creazione da zero, senza prefill esplicito)
  useEffect(() => {
    if (!isOpen) return;
    const hasPrefill = hasCreatePrefill || !!initialData?.id;
    if (hasPrefill) return;
    const draft = loadDraft();
    if (draft && (draft.business_name || draft.vat_number || draft.email)) {
      setFormData({ ...buildInitialFormData(), ...draft });
      setDraftRestored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Assegnatario di default = chi sta creando (solo se non prefillato).
  // Aspetta la lista utenti dell'org: il default va messo solo se l'utente
  // loggato ne fa parte (un superadmin che naviga un'altra org non va
  // auto-assegnato — la riga mostrerebbe "Vuoto" e si salverebbe un id
  // estraneo all'organizzazione).
  useEffect(() => {
    if (!isOpen || isEdit || formData.assigned_to || initialData?.assigned_to || !user?.id) return;
    if (!users.some((u: any) => u.id === user.id && u.active !== false)) return;
    setFormData((prev) => (prev.assigned_to ? prev : { ...prev, assigned_to: user.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id, users]);

  // Stato di default (lead o cliente) quando gli stati sono caricati
  useEffect(() => {
    if (!isOpen || isEdit || formData.stato_cliente || initialData?.stato_cliente) return;
    const candidates = clientStates
      .filter((s) => (isLead ? s.is_lead === true : s.is_lead === false))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    if (candidates.length > 0) {
      setFormData((prev) => ({ ...prev, stato_cliente: candidates[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientStates, isOpen, isLead]);

  // Pipeline dei lead nuovi: quella che si sta guardando nella pagina Lead (arriva in
  // initialData.pipeline_id), altrimenti la predefinita dell'organizzazione.
  // Va rifatto a OGNI apertura: il drawer resta montato con isOpen=false, e lo stato preso
  // al primo montaggio (o una bozza salvata) proponeva la pipeline sbagliata (Luca, 17/09/2026).
  // Una pipeline scelta a mano durante l'apertura non viene piu' toccata.
  // Storia: prima c'era anche `hasLegacyLeads` fra le condizioni e il difetto si autoalimentava
  // (NG Plast, 159 lead su 333 nati fuori da ogni pipeline).
  const pipelineSceltaAManoRef = useRef(false);
  useEffect(() => {
    if (!isOpen) pipelineSceltaAManoRef.current = false;
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || isEdit || !isLead || pipelines.length === 0) return;
    if (pipelineSceltaAManoRef.current) return;
    const daVista = initialData?.pipeline_id;
    const vistaValida = !!daVista && pipelines.some((p) => p.id === daVista && p.is_active !== false);
    const scelta = vistaValida ? daVista : pickDefaultPipeline(pipelines as PipelineCandidate[])?.id;
    if (!scelta) return;
    setFormData((prev) =>
      prev.pipeline_id === scelta
        ? prev
        : { ...prev, pipeline_id: scelta, pipeline_stage_id: vistaValida ? initialData?.pipeline_stage_id ?? null : null },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLead, pipelines, isOpen, initialData?.pipeline_id]);

  // Prefisso telefonico dal paese: si aggiorna al cambio paese SOLO se l'utente
  // non l'ha personalizzato (stessa logica di ClientFormWizard)
  const prevCountryRef = useRef<string>(formData.country);
  useEffect(() => {
    if (formData.country === prevCountryRef.current) return;
    const expectedPrev = phonePrefixForCountry(prevCountryRef.current) || '+39';
    prevCountryRef.current = formData.country;
    const next = phonePrefixForCountry(formData.country);
    if (next && formData.country_code === expectedPrev) {
      setFormData((prev) => ({ ...prev, country_code: next }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country]);

  // Stage al cambio pipeline
  useEffect(() => {
    if (!formData.pipeline_id || formData.pipeline_id === 'legacy') return;
    fetchStagesByPipeline(formData.pipeline_id);
  }, [formData.pipeline_id, fetchStagesByPipeline]);

  useEffect(() => {
    // Solo in creazione: in modifica non si auto-cambia la fase di un record esistente
    if (isEdit || !formData.pipeline_id || formData.pipeline_id === 'legacy') return;
    const active = getStagesForPipeline(formData.pipeline_id).filter((s: any) => s.is_active);
    if (active.length > 0 && !formData.pipeline_stage_id) {
      setFormData((prev) => (prev.pipeline_stage_id ? prev : { ...prev, pipeline_stage_id: active[0].id, lead_stage: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pipeline_id, formData.pipeline_stage_id, stagesByPipeline]);

  // Pipeline legacy ("Default"): prima fase lead come default, così il nuovo
  // lead non nasce "Non in pipeline" (stessa attesa delle pipeline reali)
  useEffect(() => {
    if (isEdit || !isLead || formData.lead_stage) return;
    if (formData.pipeline_id && formData.pipeline_id !== 'legacy') return;
    const first = leadStates
      .filter((s: any) => s.is_active !== false)
      .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
    if (first) {
      setFormData((prev) =>
        prev.lead_stage || (prev.pipeline_id && prev.pipeline_id !== 'legacy')
          ? prev
          : { ...prev, lead_stage: first.id },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadStates, formData.pipeline_id, formData.lead_stage, isLead]);

  const updateFormData = (updates: Record<string, any>) => {
    if ('pipeline_id' in updates) pipelineSceltaAManoRef.current = true;
    const next = { ...formDataRef.current, ...updates };
    formDataRef.current = next;
    setFormData(next);
    dirtyRef.current = true;
    if (!isEdit) saveDraft(next);
    setValidationErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const cleaned = { ...prev };
      Object.keys(updates).forEach((k) => delete cleaned[k]);
      return cleaned;
    });
    // Doppioni: in creazione tutti i campi, in modifica solo quelli cambiati (la scheda stessa esclusa)
    const changed = (k: string) => !isEdit || (next[k] || '') !== (initialData?.[k] || '');
    triggerDuplicateCheck({
      email: changed('email') ? next.email : null,
      phone: changed('phone') ? next.phone : null,
      vat_number: changed('vat_number') ? next.vat_number : null,
      tax_code: changed('tax_code') ? next.tax_code : null,
    }, { excludeClientId: isEdit ? initialData?.id : null });
  };

  const socialConfig = useSocialFields();
  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!String(formData.business_name || '').trim()) {
      errors.business_name = formData.entity_type === 'individual'
        ? 'Il nome completo è richiesto' : 'La ragione sociale è richiesta';
    }
    if (!formData.stato_cliente) errors.stato_cliente = 'Stato cliente è richiesto';
    if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Formato email non valido';
    }
    for (const f of socialConfig.fields.filter(f => f.is_visible)) {
      if (f.is_required && !formData[f.field_key]?.trim()) errors[f.field_key] = 'Campo richiesto';
      try { normalizeSocialUrl(formData[f.field_key]); } catch { errors[f.field_key] = 'Inserisci un link HTTP o HTTPS valido'; }
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scrolla alla prima riga in errore (gli errori sono inline sulla riga)
      setTimeout(() => {
        const firstError = bodyRef.current?.querySelector('.text-red-500');
        if (firstError) firstError.scrollIntoView({ block: 'center', behavior: 'smooth' });
        else bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 60);
      return false;
    }
    return true;
  };

  const handleSubmit = async (mode: SubmitMode) => {
    if (saving || isSubmitting) return;
    if (!validate()) return;
    setSaving(true);
    try {
      const submission = buildSubmissionData(formData);
      // Aliquota IVA: solo se la colonna esiste (migration 20260916d applicata).
      // In modifica, se il record passato non conteneva il campo e non è stato
      // compilato, non si invia: si cancellerebbe un'aliquota mai caricata.
      if (!vatRateSupported
        || (isEdit && !(initialData && 'default_vat_rate' in initialData) && formData.default_vat_rate === '')) {
        delete submission.default_vat_rate;
      }
      if (!vatCodesOn
        || (isEdit && !(initialData && 'vat_code_id' in initialData) && !formData.vat_code_id)) {
        delete submission.vat_code_id;
      }
      // In modifica i contacts si inviano SOLO se caricati dal client (vedi
      // clientToDrawerInitialData): senza questa guardia il diff soft-unlink
      // di updateClient scollegherebbe referenti mai mostrati nel form.
      if (isEdit && !formData.contacts_loaded) delete submission.contacts;
      const result = await onComplete(submission);
      if (!result) {
        setValidationErrors({ _general: 'Errore durante il salvataggio. Riprova o contatta il supporto.' });
        return;
      }
      clearDraft();
      resetDuplicateCheck();
      dirtyRef.current = false;
      if (mode === 'another') {
        const kept: Record<string, any> = {};
        for (const key of KEEP_ON_CREATE_ANOTHER) kept[key] = formData[key];
        setFormData({ ...buildInitialFormData(), ...kept });
        setDraftRestored(false);
        bodyRef.current?.scrollTo({ top: 0 });
      } else {
        if (mode === 'open' && onOpenClient && result?.id) onOpenClient(result.id);
        // Il componente resta montato a drawer chiuso: senza reset, alla
        // riapertura si ritroverebbero i dati del record appena creato
        setFormData(buildInitialFormData(initialData));
        setDraftRestored(false);
        onClose();
      }
    } catch (error) {
      console.error('CreateClientDrawer submit error:', error);
      setValidationErrors({ _general: 'Errore durante il salvataggio. Riprova o contatta il supporto.' });
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (dirtyRef.current && formData.business_name) {
      const msg = isEdit
        ? 'Chiudere senza salvare? Le modifiche andranno perse.'
        : 'Chiudere? La bozza resta salvata e la ritrovi riaprendo il form.';
      if (!window.confirm(msg)) return;
    }
    onClose();
  };

  useDrawerEscape(isOpen,requestClose,10001);

  // Tastiera: Esc chiude (con guardia), Cmd/Ctrl+Invio crea
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); requestClose(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleSubmit('close'); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, formData, saving]);

  // VIES: compila SOLO i campi vuoti (mai sovrascrivere l'input dell'utente).
  // Legge dalla ref: il callback può arrivare in ritardo rispetto a ciò che
  // l'utente ha digitato nel frattempo.
  const handleVatEnrich = (data: EnrichResult) => {
    const current = formDataRef.current;
    const updates: Record<string, any> = {};
    if (data.business_name && !current.business_name) updates.business_name = data.business_name;
    if (data.street_address && !current.address) {
      updates.address = data.street_number
        ? `${data.street_address} ${data.street_number}`
        : data.street_address;
    }
    if (data.postal_code && !current.postal_code) updates.postal_code = data.postal_code;
    if (data.city && !current.city) updates.city = data.city;
    if (data.province && !current.province) updates.province = data.province;
    if (data.country && !current.country) updates.country = data.country;
    if (Object.keys(updates).length > 0) updateFormData(updates);
  };

  const discardDraft = () => {
    clearDraft();
    resetDuplicateCheck();
    const fresh = buildInitialFormData(initialData);
    formDataRef.current = fresh;
    setFormData(fresh);
    setValidationErrors({});
    setDraftRestored(false);
    dirtyRef.current = false;
  };

  // Animazione apertura/chiusura: resta montato durante l'uscita, il pannello
  // scorre da destra e l'overlay fa fade (doppio rAF per far partire la transizione).
  const [shouldRender, setShouldRender] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const t = setTimeout(() => setShouldRender(false), 220);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Chiudi il popover di conferma reset al click fuori
  useEffect(() => {
    if (!showResetConfirm) return;
    const onDown = (e: MouseEvent) => {
      if (resetRef.current && !resetRef.current.contains(e.target as Node)) setShowResetConfirm(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showResetConfirm]);

  const classificationSummary = useMemo(() => {
    const parts: string[] = [];
    if ((formData.selected_tags || []).length) parts.push(`${formData.selected_tags.length} tag`);
    const settoreNames = (formData.selected_settori || [])
      .map((id: string) => settori.find((s: any) => s.id === id)?.name)
      .filter(Boolean);
    if (settoreNames.length) {
      parts.push(settoreNames.length <= 2 ? settoreNames.join(', ') : `${settoreNames.length} settori`);
    }
    if ((formData.interest_categories || []).length) parts.push(`${formData.interest_categories.length} interessi`);
    return parts.join(' · ');
  }, [formData.selected_tags, formData.selected_settori, formData.interest_categories, settori]);

  if (!shouldRender) return null;

  const isCompany = formData.entity_type === 'company';
  const busy = saving || isSubmitting;

  return (
    <div className="fixed inset-0 z-[10001] overflow-hidden" role="dialog" aria-label={isEdit ? 'Modifica anagrafica' : 'Nuova anagrafica'}>
      {/* Overlay leggero: la pagina dietro resta intravedibile */}
      <div
        className={`absolute inset-0 bg-black/10 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={requestClose}
      />
      <div className={`absolute inset-y-0 right-0 flex w-full max-w-[600px] flex-col bg-white dark:bg-[#202020] shadow-xl border-l border-[rgba(55,53,47,0.16)] dark:border-white/10 transition-transform duration-200 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4">
          <span className="text-[11px] font-semibold uppercase tracking-widest bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
            {isEdit ? 'Modifica anagrafica' : 'Nuova anagrafica'}
          </span>
          <div className="flex items-center gap-1">
            {/* Reset dati con conferma attaccata al bottone */}
            <div ref={resetRef} className="relative">
              <button
                type="button"
                onClick={() => setShowResetConfirm((v) => !v)}
                title={isEdit ? 'Ripristina i dati del record' : 'Azzera tutti i dati inseriti'}
                aria-label="Reimposta dati"
                className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
              >
                <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
              </button>
              {showResetConfirm && (
                <div className="absolute right-0 top-full z-50 mt-1 w-60 rounded-md border border-[rgba(55,53,47,0.16)] bg-white p-3 shadow-lg dark:border-white/15 dark:bg-[#252525]">
                  <p className="text-[12px] text-[#37352F] dark:text-white">
                    {isEdit
                      ? 'Ripristinare i dati originali del record? Le modifiche non salvate andranno perse.'
                      : 'Azzerare tutti i dati inseriti? Anche la bozza salvata viene eliminata.'}
                  </p>
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowResetConfirm(false)}
                      className="rounded px-2 py-1 text-[12px] font-medium text-[#37352F] hover:bg-[rgba(55,53,47,0.08)] dark:text-white dark:hover:bg-white/[0.06]"
                    >
                      Annulla
                    </button>
                    <button
                      type="button"
                      onClick={() => { discardDraft(); setShowResetConfirm(false); }}
                      className="rounded bg-[#EB5757] px-2.5 py-1 text-[12px] font-semibold text-white hover:opacity-90"
                    >
                      {isEdit ? 'Ripristina' : 'Azzera'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              type="button" onClick={requestClose} aria-label="Chiudi"
              className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 pb-4">
          {/* Segmented tipo */}
          <div className="mt-2 inline-flex rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/15 overflow-hidden">
            {([['company', 'Azienda', Building2], ['individual', 'Persona fisica', User]] as const).map(([value, label, Icon], i) => (
              <button
                key={value} type="button"
                onClick={() => updateFormData({ entity_type: value })}
                className={`flex items-center gap-1.5 px-3 py-1 text-[12.5px] transition-colors ${i === 1 ? 'border-l border-[rgba(55,53,47,0.16)] dark:border-white/15' : ''} ${
                  formData.entity_type === value
                    ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08] font-semibold text-[#37352F] dark:text-white'
                    : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)]'
                }`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                {label}
              </button>
            ))}
          </div>

          {/* Titolo = ragione sociale / nome completo */}
          <div className="mt-3 text-[10px] font-medium uppercase tracking-wider text-[rgba(55,53,47,0.45)] dark:text-gray-500">
            {isCompany ? 'Ragione sociale · obbligatoria' : 'Nome completo · obbligatorio'}
          </div>
          <input
            type="text"
            value={formData.business_name}
            onChange={(e) => updateFormData({ business_name: e.target.value })}
            placeholder={isCompany ? 'Ragione sociale' : 'Nome e cognome'}
            className="w-full bg-transparent text-[17px] font-semibold text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.35)] dark:placeholder-gray-500 focus:outline-none py-1"
            autoFocus
          />
          {validationErrors.business_name && (
            <p className="text-xs text-red-500">{validationErrors.business_name}</p>
          )}

          {/* P.IVA-first: incolla la partita IVA e VIES compila il resto */}
          {isCompany && (
            <div className="mt-1">
              <VatEnrichField
                value={formData.vat_number}
                onChange={(value) => updateFormData({ vat_number: value })}
                onEnrich={handleVatEnrich}
                error={validationErrors.vat_number}
                label={fiscalVariantForCountry(formData.country) === 'extra' ? 'Identificativo fiscale' : 'Partita IVA'}
                viesEnabled={fiscalVariantForCountry(formData.country) !== 'extra'}
                compact
              />
            </div>
          )}

          {/* Bozza ripristinata */}
          {draftRestored && (
            <div className="mt-2 flex items-center justify-between rounded-md bg-black/[0.03] dark:bg-white/[0.05] px-3 py-2 text-[12px] text-[#6B6762] dark:text-gray-300">
              <span>Bozza ripristinata dall'ultima sessione.</span>
              <button type="button" onClick={discardDraft} className="text-[#2383E2] hover:underline">Scarta</button>
            </div>
          )}


          {validationErrors._general && (
            <p className="mt-2 rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-[12px] text-red-700 dark:text-red-300">
              {validationErrors._general}
            </p>
          )}

          {/* Proprietà principali */}
          <div className="mt-3">
            <CreateClientTopProps formData={formData} updateFormData={updateFormData} validationErrors={validationErrors} />
          </div>

          {/* Sezioni */}
          <div className="mt-4 space-y-1">
            <DrawerSection title="Dati aziendali e fiscali" defaultOpen summary={formData.vat_number ? `P.IVA ${formData.vat_number}` : undefined}>
              <FiscalSection formData={formData} updateFormData={updateFormData} validationErrors={validationErrors} />
              <DuplicateNotice result={duplicateResult} reasons={['Partita IVA', 'Codice Fiscale']} onOpenClient={onOpenClient ? (id: string) => { onOpenClient(id); onClose(); } : undefined} draftKept={!isEdit} />
              {vatRateSupported && (
                <PropSelectRow
                  icon={<Percent className="h-3 w-3" strokeWidth={1.5} />}
                  label="Aliquota IVA"
                  value={formData.default_vat_rate === '' ? '' : String(formData.default_vat_rate)}
                  onChange={(value) => updateFormData({ default_vat_rate: value === 'none' ? '' : value })}
                  options={[
                    { value: 'none', label: 'Nessuna' },
                    ...VAT_RATE_OPTIONS.map((rate) => ({ value: String(rate), label: `${rate}%` })),
                  ]}
                  placeholder="Vuoto"
                />
              )}
              {vatCodesOn && vatCodes.length > 0 && (
                <PropSelectRow
                  icon={<Percent className="h-3 w-3" strokeWidth={1.5} />}
                  label="Codice IVA"
                  value={formData.vat_code_id || ''}
                  onChange={(value) => {
                    // Il codice porta la sua aliquota: la si allinea, l'offerta usa quella
                    const code = vatCodes.find((c) => c.id === value);
                    updateFormData(code
                      ? { vat_code_id: code.id, default_vat_rate: String(Number(code.rate)) }
                      : { vat_code_id: '' });
                  }}
                  options={[
                    { value: 'none', label: 'Nessuno' },
                    ...vatCodes.map((c) => ({ value: c.id, label: vatCodeLabel(c) })),
                  ]}
                  placeholder="Vuoto"
                />
              )}
            </DrawerSection>

            <DrawerSection title="Sede legale" defaultOpen summary={[formData.city, formData.country].filter(Boolean).join(', ') || undefined}>
              <AddressSectionRows formData={formData} updateFormData={updateFormData} validationErrors={validationErrors} />
            </DrawerSection>

            {/* Sedi operative (lista, come già prima) e Indirizzi di spedizione (Sei Farm, 02/09/2026):
                stessa tabella client_operational_locations, distinte da location_type */}
            {(() => {
              const all: any[] = formData.operational_locations || [];
              const isShip = (l: any) => l.location_type === 'shipping';
              const operational = all.filter((l) => !isShip(l));
              const shipping = all.filter(isShip);
              const legal = { label: 'Sede legale', data: formData };
              const summaryOf = (list: any[], one: string, many: string) => {
                if (!list.length) return undefined;
                const def = list.find((l) => l.is_default);
                return `${list.length} ${list.length === 1 ? one : many}${def?.name ? ` · ${def.name}` : ''}`;
              };
              return (
                <>
                  <DrawerSection title="Sedi operative" defaultOpen summary={summaryOf(operational, 'sede', 'sedi')}>
                    <OperationalLocationsRows
                      variant="operational"
                      locations={operational}
                      onChange={(list) => updateFormData({ operational_locations: [...list, ...shipping] })}
                      copySources={[legal]}
                    />
                  </DrawerSection>
                  <DrawerSection title="Indirizzi di spedizione" defaultOpen summary={summaryOf(shipping, 'indirizzo', 'indirizzi')}>
                    <OperationalLocationsRows
                      variant="shipping"
                      locations={shipping}
                      onChange={(list) => updateFormData({ operational_locations: [...operational, ...list] })}
                      copySources={[legal, ...operational.map((l) => ({ label: l.name || 'Sede operativa', data: l }))]}
                    />
                  </DrawerSection>
                </>
              );
            })()}

            <DrawerSection title="Recapiti e referenti" defaultOpen summary={formData.email || undefined}>
              <ContactRows formData={formData} updateFormData={updateFormData} validationErrors={validationErrors} />
              <DuplicateNotice result={duplicateResult} reasons={['Email', 'Telefono']} onOpenClient={onOpenClient ? (id: string) => { onOpenClient(id); onClose(); } : undefined} draftKept={!isEdit} />
              {/* In modifica i referenti compaiono solo se caricati dal client
                  (contacts_loaded), per non rischiare il diff soft-unlink su
                  una lista vuota mai letta dal DB */}
              {(!isEdit || formData.contacts_loaded) && (
                <ReferentiRows formData={formData} updateFormData={updateFormData} />
              )}
            </DrawerSection>

            <DrawerSection title="Social"><SocialFields values={formData} onChange={updateFormData} errors={validationErrors} /></DrawerSection>

            <DrawerSection title="Marketing e consensi" summary={[
              formData.marketing_consent_email ? 'Email sì' : null,
              formData.newsletter_consent ? 'Newsletter sì' : null,
              formData.marketing_consent_date
                ? `Consenso del ${new Date(formData.marketing_consent_date).toLocaleDateString('it-IT')}`
                : null,
            ].filter(Boolean).join(' · ') || undefined}>
              <ConsentRows formData={formData} updateFormData={updateFormData} validationErrors={validationErrors} />
            </DrawerSection>

            <DrawerSection title="Classificazione" summary={classificationSummary || undefined}>
              <MultiChipRow
                icon={<Layers className="h-3 w-3" strokeWidth={1.5} />}
                label="Tag"
                selected={formData.selected_tags || []}
                options={tags.map((t: any) => ({ value: t.id, label: t.name, color: t.color }))}
                onAdd={(id) => updateFormData({ selected_tags: [...(formData.selected_tags || []), id] })}
                onRemove={(id) => updateFormData({ selected_tags: (formData.selected_tags || []).filter((x: string) => x !== id) })}
                onCreate={async (name) => {
                  const created = await createTag({ name } as any);
                  if (created?.id) updateFormData({ selected_tags: [...(formData.selected_tags || []), created.id] });
                }}
                addPlaceholder="Aggiungi tag..."
              />
              <MultiChipRow
                icon={<Factory className="h-3 w-3" strokeWidth={1.5} />}
                label="Settore"
                selected={formData.selected_settori || []}
                options={settori.filter((x: any) => x.is_active).map((x: any) => ({ value: x.id, label: x.name, color: (x as any).color }))}
                onAdd={(id) => updateFormData({ selected_settori: [...(formData.selected_settori || []), id] })}
                onRemove={(id) => updateFormData({ selected_settori: (formData.selected_settori || []).filter((v: string) => v !== id) })}
                addPlaceholder="Aggiungi settore..."
              />
              <InterestTreeChipRow
                selected={formData.interest_categories || []}
                onChange={(ids) => updateFormData({ interest_categories: ids })}
              />
              <PropTextRow
                icon={<FileText className="h-3 w-3" strokeWidth={1.5} />}
                label="Note interessi"
                value={formData.interest_notes || ''}
                onChange={(value) => updateFormData({ interest_notes: value })}
              />
            </DrawerSection>

            {(canAccessPaymentMethods || canAccessInventory) && (
              <DrawerSection title="Pagamenti e listini" summary={[
                paymentMethods.find((m) => m.id === formData.default_payment_method_id)?.name,
                priceLists.find((p) => p.id === formData.default_price_list_id)?.name,
              ].filter(Boolean).join(' · ') || undefined}>
                <div>
                  {canAccessPaymentMethods && (
                    <PropSelectRow
                      icon={<CreditCard className="h-3 w-3" strokeWidth={1.5} />}
                      label="Metodo pagam."
                      value={formData.default_payment_method_id}
                      onChange={(value) => updateFormData({ default_payment_method_id: value })}
                      options={paymentMethods.map((m) => ({ value: m.id, label: m.name }))}
                      placeholder="Vuoto"
                    />
                  )}
                  {canAccessInventory && (
                    <PropSelectRow
                      icon={<DollarSign className="h-3 w-3" strokeWidth={1.5} />}
                      label="Listino prezzi"
                      value={formData.default_price_list_id}
                      onChange={(value) => updateFormData({ default_price_list_id: value })}
                      options={priceLists.map((p) => ({ value: p.id, label: p.name }))}
                      placeholder="Vuoto"
                    />
                  )}
                </div>
              </DrawerSection>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 px-4 py-3">
          {/* In creazione i 3 bottoni riempiono la riga e la hint si troncava
              in modo illeggibile: resta solo come tooltip del footer */}
          <span
            className="min-w-0 flex-1 truncate text-[11px] text-[rgba(55,53,47,0.45)] dark:text-gray-500"
            title={isEdit ? 'Cmd Invio salva, Esc chiude' : 'Cmd Invio crea, Esc chiude'}
          >
            {isEdit ? 'Cmd Invio salva · Esc chiude' : ''}
          </span>
          {!isEdit && (
            <button
              type="button" disabled={busy} onClick={() => handleSubmit('another')}
              className="whitespace-nowrap rounded px-2 py-1.5 text-[12.5px] font-medium text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] disabled:opacity-50"
            >
              Crea e aggiungine un'altra
            </button>
          )}
          {!isEdit && (
            <button
              type="button" disabled={busy} onClick={() => handleSubmit('open')}
              className="whitespace-nowrap rounded border border-[rgba(55,53,47,0.16)] dark:border-white/15 px-2.5 py-1.5 text-[12.5px] font-medium text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] disabled:opacity-50"
            >
              Crea e apri scheda
            </button>
          )}
          <button
            type="button" disabled={busy} onClick={() => handleSubmit('close')}
            className="whitespace-nowrap rounded bg-[#37352F] px-3.5 py-1.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50 dark:bg-white dark:text-[#191919]"
          >
            {busy ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea'}
          </button>
        </div>
      </div>
    </div>
  );
}
